import {
  type AskPunttProjectFile,
  type AskPunttProjectFileVersion,
} from "@mg/schemas/src/christo/askPunttProject";
import {
  CaretDown,
  CircleNotch,
  FileText,
  ImageSquare,
  PencilSimple,
  VideoCamera,
} from "@phosphor-icons/react";
import { useState } from "react";

import { downloadAsset } from "../../../utils/http";
import { displayFileNameFromSource } from "../utils/projectContextDocuments";

/**
 * Right-hand "PROJECT CONTEXT" panel for the project workspace. Renders the
 * project's versioned files (read-only in v1) — the same artifacts the agent
 * is given as project context. Owners get a pencil affordance to edit the
 * project's description / metadata.
 */
/** Artwork or context asset from a ticket, not yet a versioned project file. */
export interface CampaignExtraDocument {
  source: string;
  name: string;
  type: string;
  kind: "artwork" | "context";
}

function fileTypeIcon(type: string) {
  if (type === "image") return ImageSquare;
  if (type === "video") return VideoCamera;
  return FileText;
}

function fileTypeLabel(type: string, name: string): string {
  const ext = name.split(".").pop()?.toUpperCase();
  if (ext && ext.length <= 5 && ext !== name.toUpperCase()) return ext;
  if (type === "image") return "IMAGE";
  if (type === "video") return "VIDEO";
  return "PDF";
}

export function CampaignDocsPanel({
  files,
  extraDocuments = [],
  description,
  onEdit,
}: {
  files: AskPunttProjectFile[];
  /**
   * Artwork and context assets from review tickets that are not already
   * represented as versioned project files.
   */
  extraDocuments?: CampaignExtraDocument[];
  /** The project's overall description, surfaced above the documents. */
  description?: string;
  /** Owner-only edit affordance for the project description / metadata. */
  onEdit?: () => void;
}) {
  const [panelOpen, setPanelOpen] = useState(true);
  const [docsOpen, setDocsOpen] = useState(true);
  const totalCount = files.length + extraDocuments.length;

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white"
      aria-label="Project context"
      data-testid="project-context-panel"
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-puntt-neutral-gray-5 px-4 py-3">
        <button
          type="button"
          onClick={() => setPanelOpen((o) => !o)}
          aria-expanded={panelOpen}
          aria-label="Toggle project context section"
          data-testid="project-context-toggle"
          className="flex min-w-0 flex-1 items-center gap-2 bg-base-white text-left"
        >
          <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
            Project context
          </h2>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${panelOpen ? "" : "-rotate-90"}`}
          />
        </button>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit project context"
            data-testid="project-context-edit-button"
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
          >
            <PencilSimple size={15} />
          </button>
        )}
      </div>

      {panelOpen && (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
          {description && (
            <div data-testid="campaign-project-description">
              <h3 className="text-[13px] font-bold text-puntt-neutral-gray-12">
                Instructions
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                {description}
              </p>
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={() => setDocsOpen((o) => !o)}
              aria-expanded={docsOpen}
              aria-label="Toggle reference documents"
              data-testid="campaign-docs-toggle"
              className="flex w-full items-center gap-2 rounded-md bg-base-white px-2 py-2 text-left"
            >
              <h3 className="flex-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                Reference Documents
              </h3>
              <CaretDown
                size={14}
                aria-hidden="true"
                className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${docsOpen ? "" : "-rotate-90"}`}
              />
            </button>

            {docsOpen && (
              <div
                className="mt-2 flex flex-col gap-2"
                data-testid="campaign-docs-body"
              >
                {totalCount === 0 ? (
                  <p
                    className="text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                    data-testid="campaign-docs-empty"
                  >
                    No documents yet. Artwork and context documents attached to
                    this project show up here.
                  </p>
                ) : (
                  <>
                    {files.map((file) => (
                      <ReferenceFileRow key={file.fileId} file={file} />
                    ))}
                    {extraDocuments.map((doc, idx) => (
                      <ReferenceFileRow
                        key={`extra-${doc.source}`}
                        source={doc.source}
                        name={doc.name}
                        type={doc.type}
                        testId={`campaign-extra-doc-${idx}`}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function latestVersion(
  file: AskPunttProjectFile,
): AskPunttProjectFileVersion | undefined {
  return (
    file.versions.find((v) => v.versionNumber === file.currentVersion) ??
    file.versions[file.versions.length - 1]
  );
}

function projectFileDisplayName(file: AskPunttProjectFile): string {
  const trimmed = file.name?.trim();
  if (trimmed && trimmed !== "File") return trimmed;
  const latest = latestVersion(file);
  return (
    displayFileNameFromSource(latest?.name) ??
    displayFileNameFromSource(latest?.source) ??
    displayFileNameFromSource(latest?.key) ??
    trimmed ??
    "File"
  );
}

function ReferenceFileRow({
  file,
  source,
  name,
  type,
  testId,
}: {
  file?: AskPunttProjectFile;
  source?: string;
  name?: string;
  type?: string;
  testId?: string;
}) {
  const resolvedSource = source ?? latestVersion(file!)?.source ?? "";
  const resolvedName = name ?? (file ? projectFileDisplayName(file) : "File");
  const resolvedType = type ?? file?.type ?? "file";
  const rowTestId =
    testId ?? (file ? `campaign-file-download-${file.fileId}` : "campaign-doc");

  return (
    <DownloadableReferenceRow
      source={resolvedSource}
      name={resolvedName}
      type={resolvedType}
      testId={rowTestId}
    />
  );
}

function DownloadableReferenceRow({
  source,
  name,
  type,
  testId,
}: {
  source: string;
  name: string;
  type: string;
  testId: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const TypeIcon = fileTypeIcon(type);
  const typeLabel = fileTypeLabel(type, name);

  const handleDownload = async () => {
    if (downloading || !source) return;
    setDownloading(true);
    try {
      await downloadAsset(source, name);
    } catch (err) {
      console.error("Failed to download project context file", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading || !source}
      data-testid={testId}
      aria-label={`Download ${name}`}
      className="flex w-full items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors hover:text-puntt-neutral-gray-12 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center text-puntt-neutral-gray-11"
      >
        {downloading ? (
          <CircleNotch size={18} className="animate-spin" />
        ) : (
          <TypeIcon size={18} />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
        {name}
      </span>
      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
        {typeLabel}
      </span>
    </button>
  );
}
